var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useCallback, useDeferredValue, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { ButtonAFY, FormDocumentsDownloadAFY, variants, } from "@affinitysystemsgroup/github-packages-affinity-components/dist";
import { themeCore } from "../../../assets/themes/theme";
import { useCertificatesPoller, } from "../hooks/certificatesPoller";
import { fetchQuoteCertificates } from "../hooks/fetchQuoteCertificates";
import { useCertificatesSectionStyles } from "./certificatesSection.styles";
export var CERTIFICATES_GENERATING_TEXT = "Estamos generando tus certificados. Esto puede tardar unos segundos.";
export var CERTIFICATES_TIMEOUT_TEXT = "La generación está tardando más de lo habitual.";
export var CERTIFICATES_ERROR_TEXT = "No pudimos consultar los certificados.";
export var CERTIFICATES_REFRESH_LABEL = "Actualizar";
// Module-level so the hook receives a stable options object on every render.
var POLLER_OPTIONS = {
    fetchCertificates: fetchQuoteCertificates,
};
var toDocumentProps = function (certificate) {
    var _a;
    return ({
        id: certificate.id,
        name: certificate.name,
        url: (_a = certificate.url) !== null && _a !== void 0 ? _a : undefined,
        status: certificate.status,
    });
};
/** One more row of the documents card, aligned with the document list column. */
var StatusRow = function (_a) {
    var testId = _a.testId, children = _a.children;
    var classes = useCertificatesSectionStyles().classes;
    return (_jsx("div", __assign({ className: classes.footer }, { children: _jsx("div", __assign({ className: classes.status, role: "status", "data-testid": testId }, { children: children })) })));
};
var RefreshButton = function (_a) {
    var onClick = _a.onClick;
    return (_jsx(ButtonAFY, { variant: variants.text, label: CERTIFICATES_REFRESH_LABEL, color: themeCore.colors.primaryText, fontSize: themeCore.typography.fontSizeSm, fontWeight: 600, heightButton: "auto", onClick: onClick }));
};
/**
 * The documents section of the payment confirmation screen.
 *
 * It is the ONLY subscriber of the certificates poller: `useSyncExternalStore` (inside
 * `useCertificatesPoller`) re-renders this memoized component when a poll changes something,
 * while the summary, the email box and the header stay untouched. The document list is
 * derived through `useDeferredValue` so a burst of updates never blocks the user's typing in
 * the email field.
 *
 * The section is the card: `FormDocumentsDownloadAFY` offers no slot for extra content, so the
 * section reproduces its surface and drops the block's own shadow (see the styles module),
 * which keeps the polling status inside the same card as the title and the document list.
 */
export var CertificatesSection = memo(function CertificatesSection(_a) {
    var quoteId = _a.quoteId, title = _a.title, generatingTitle = _a.generatingTitle, initialCertificates = _a.initialCertificates, disabledDownload = _a.disabledDownload, onDownload = _a.onDownload;
    var classes = useCertificatesSectionStyles().classes;
    var _b = useCertificatesPoller(quoteId, POLLER_OPTIONS, initialCertificates), certificates = _b.certificates, isGenerating = _b.isGenerating, timedOut = _b.timedOut, error = _b.error, refresh = _b.refresh;
    var deferredCertificates = useDeferredValue(certificates);
    var documents = useMemo(function () { return deferredCertificates.map(toDocumentProps); }, [deferredCertificates]);
    var handleDownload = useCallback(function (document) {
        if (!document.url)
            return;
        onDownload(document);
    }, [onDownload]);
    var handleRefresh = useCallback(function () {
        console.log("[CertificatesSection] Manual refresh requested:", { quoteId: quoteId });
        refresh();
    }, [quoteId, refresh]);
    return (_jsxs("section", __assign({ className: classes.card, "aria-busy": isGenerating, "aria-live": "polite", "data-testid": "certificates-section" }, { children: [_jsx(FormDocumentsDownloadAFY, { title: isGenerating ? generatingTitle : title, documents: documents, onClickDownload: handleDownload, visible: true, disabledDownload: disabledDownload, completed: true }), isGenerating && (_jsxs(StatusRow, __assign({ testId: "certificates-generating" }, { children: [_jsx("span", __assign({ className: classes.spinner }, { children: _jsx(CircularProgress, { size: 18, thickness: 5, color: "inherit", "aria-label": "Generando certificados" }) })), _jsx("span", { children: CERTIFICATES_GENERATING_TEXT })] }))), timedOut && (_jsxs(StatusRow, __assign({ testId: "certificates-timeout" }, { children: [_jsx("span", { children: CERTIFICATES_TIMEOUT_TEXT }), _jsx(RefreshButton, { onClick: handleRefresh })] }))), !isGenerating && !timedOut && error && (_jsxs(StatusRow, __assign({ testId: "certificates-error" }, { children: [_jsx("span", { children: CERTIFICATES_ERROR_TEXT }), _jsx(RefreshButton, { onClick: handleRefresh })] })))] })));
});
